import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { actsAsOwner } from './ownership';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import Loader from '../../../shared/components/ui/Loader';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import { deleteManagementTask, getManagementTasks } from '../../services/api/implementation/impl';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import { useForm } from 'react-hook-form';
import { Plus } from 'lucide-react';

import CreateTaskModal from './CreateTaskModal';
import EditTaskModal from './EditTaskModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import TaskCard, { Chip } from './TaskCard';
import CardGridEmpty from './CardGridEmpty';
import UserAvatar from '../../../shared/components/ui/UserAvatar';
import TaskBadges from './TaskBadges';
import TaskProgress from './TaskProgress';
import { statusChipClass } from './taskWorkflow';
import { displayRootStatus } from './taskTree';
import { formatRupees } from './budget';
import { isLegalCaseTask, kindChipClass, kindLabel, TaskKind } from './permission';

// Narrows the task grid by type; '' is every type. `empty` names what the tab
// found nothing of, so the empty panel reads for the tab that is open.
const KIND_FILTERS = [
    { key: '', label: 'All', empty: 'No tasks added yet' },
    { key: TaskKind.GENERAL, label: 'General', empty: 'No general tasks yet' },
    { key: TaskKind.PERMISSION, label: 'Permission', empty: 'No permission tasks yet' },
    { key: TaskKind.COMPLIANCE, label: 'Compliance', empty: 'No compliance tasks yet' },
    { key: TaskKind.LEGAL_CASE, label: 'Legal Court Cases', empty: 'No legal court cases yet' }
];

// `lockProject` pins the list to a project supplied by the host (the project
// page) and hides the picker; `onSelectTask` lets that host open the task inline
// instead of navigating away to the standalone detail page.
const ManagementLiveTasks = ({
    embedded = false,
    lockProject = false,
    projectId: lockedProjectId = null,
    onSelectTask = null
}) => {
    const { collapsed } = useSidebar();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { projectsForDropdown } = useProjectsList();
    const [ownProjectId, setOwnProjectId] = useState(null);
    const projectId = lockProject ? lockedProjectId : ownProjectId;
    const [tasks, setTasks] = useState([]);
    const kindParam = searchParams.get('kind') || '';
    const kindFilter = KIND_FILTERS.some((filter) => filter.key === kindParam) ? kindParam : '';

    const setKindFilter = (next) => {
        const params = new URLSearchParams(searchParams);
        if (next) {
            params.set('kind', next);
        } else {
            params.delete('kind');
        }
        setSearchParams(params, { replace: true });
    };
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [deletingTask, setDeletingTask] = useState(null);
    const queryClient = useQueryClient();

    const deleteMutation = useMutation({
        mutationFn: (taskId) => deleteManagementTask(taskId),
        onSuccess: () => {
            toast.success('Task deleted');
            setDeletingTask(null);
            queryClient.invalidateQueries(['managementTasks']);
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to delete task');
        }
    });

    const openTask = (taskId) => {
        if (onSelectTask) {
            onSelectTask(taskId);
            return;
        }
        navigate(`/builder/management/tasks/${taskId}`);
    };

    const { register, watch, setValue } = useForm();
    const selectedProject = watch('project');

    useEffect(() => {
        if (lockProject) return;
        if (projectsForDropdown?.length > 0 && !selectedProject) {
            setValue('project', projectsForDropdown[0].id);
            setOwnProjectId(projectsForDropdown[0].id);
        }
    }, [lockProject, projectsForDropdown, setValue, selectedProject]);

    useEffect(() => {
        if (lockProject) return;
        if (selectedProject) {
            setOwnProjectId(selectedProject);
        }
    }, [lockProject, selectedProject]);

    const { data: tasksData, isLoading } = useQuery({
        queryKey: ['managementTasks', projectId, kindFilter],
        queryFn: () => getManagementTasks({
            projectId,
            limit: 50,
            kind: kindFilter || undefined
        }),
        enabled: !!projectId,
    });

    useEffect(() => {
        if (tasksData?.data) {
            const rows = Array.isArray(tasksData.data) ? tasksData.data : [];
            // An API without the kind filter returns every type; narrowing here
            // too keeps a type tab from ever showing another type.
            setTasks(
                kindFilter
                    ? rows.filter((task) => (task.kind || TaskKind.GENERAL) === kindFilter)
                    : rows
            );
        }
    }, [tasksData, kindFilter]);

    const formatDate = (dateString) => {
        if (!dateString) return '---';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const toolbar = (
        <div className={`flex flex-col md:flex-row justify-between items-stretch md:items-end gap-3 ${embedded ? 'bg-white px-4 md:px-6 py-3 border-b' : ''}`}>
            <div className="flex flex-wrap items-end gap-4 w-full md:w-auto">
                {!lockProject && (
                    <div className="w-full md:w-[18rem]">
                        <FormInput
                            width={100}
                            label={'Project'}
                            register={register}
                            register_key={'project'}
                            type={'dropdown'}
                            options={projectsForDropdown}
                        />
                    </div>
                )}
                <div
                    role="group"
                    aria-label="Task type"
                    className="flex min-h-10 shrink-0 flex-wrap items-center gap-1 rounded-lg border border-gray-200 p-1">
                    {KIND_FILTERS.map((filter) => (
                        <button
                            key={filter.key || 'all'}
                            type="button"
                            onClick={() => setKindFilter(filter.key)}
                            className={`px-3 py-1.5 text-sm rounded whitespace-nowrap ${
                                kindFilter === filter.key
                                    ? 'bg-[#346C75] text-white'
                                    : 'text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                            {filter.label}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );

    const priorityTone = (priority) =>
        priority === 'High' ? 'rose' : priority === 'Medium' ? 'amber' : 'positive';

    const hasTasks = tasks?.length > 0;
    const activeFilter = KIND_FILTERS.find((filter) => filter.key === kindFilter);

    const table = (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 pb-4">
            {hasTasks ? (
                <>
                    {/* Creating starts from the grid itself, as on the Company and
                        Project lists — so the first cell is the add tile. An empty
                        grid carries the same invite inside the empty panel instead,
                        so the two never sit side by side. */}
                    <button
                        type="button"
                        onClick={() => setIsCreateModalOpen(true)}
                        className="w-full min-h-[220px] rounded-2xl border-2 border-dashed border-[#BFD4D8] bg-white text-[#2A565E] flex flex-col items-center justify-center gap-3 shadow-sm hover:bg-[#F7FBFC] hover:border-[#9EC5CC] transition-colors">
                        <span className="h-11 w-11 rounded-full bg-[#E8F3F5] flex items-center justify-center">
                            <Plus size={22} />
                        </span>
                        <span className="text-base font-semibold">Add Task</span>
                    </button>

                    {tasks.map((task) => (
                        <TaskCard
                            key={task.id}
                            title={task.title}
                            onOpen={() => openTask(task.id)}
                            onEdit={() => {
                                setSelectedTask(task);
                                setIsEditModalOpen(true);
                            }}
                            onDelete={
                                actsAsOwner(task)
                                    ? () => setDeletingTask(task)
                                    : undefined
                            }
                            subtitle={
                                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                    <span
                                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-semibold ${statusChipClass(displayRootStatus(task))}`}>
                                        {displayRootStatus(task) || '---'}
                                    </span>
                                    <span
                                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-semibold ${kindChipClass(task.kind)}`}>
                                        {kindLabel(task.kind)}
                                    </span>
                                </div>
                            }
                        >
                            <div className="flex items-center gap-2 text-base text-[#4B5563] min-w-0">
                                <UserAvatar name={task.owner?.name} size={28} />
                                <span className="truncate">{task.owner?.name || '---'}</span>
                            </div>

                            {!isLegalCaseTask(task) && <TaskProgress progress={task.progress} />}

                            <div className="flex items-center gap-2 flex-wrap">
                                <Chip tone={priorityTone(task.priority)}>{task.priority || '---'}</Chip>
                                {!isLegalCaseTask(task) && (
                                    <Chip tone="positive">Budget: {formatRupees(task.budget)}</Chip>
                                )}
                                <Chip tone="indigo">
                                    {isLegalCaseTask(task) ? 'Target' : 'Due'}: {formatDate(task.dueDate)}
                                </Chip>
                            </div>

                            <TaskBadges badges={task.badges} className="flex flex-wrap gap-1" />
                        </TaskCard>
                    ))}
                </>
            ) : (
                // Held back while the first fetch is in flight so the panel does
                // not flash before the grid has any tasks to show.
                !isLoading && (
                    <CardGridEmpty
                        message={activeFilter?.empty || 'No tasks added yet'}
                        hint={
                            kindFilter
                                ? 'Add one, or switch to another type.'
                                : 'Add your first task to get started.'
                        }
                        action={
                            <Button
                                type="button"
                                displayType="primary"
                                onClick={() => setIsCreateModalOpen(true)}>
                                Add Task
                            </Button>
                        }
                    />
                )
            )}
        </div>
    );

    const modals = (
        <>
            <Loader isVisible={isLoading} />
            {isCreateModalOpen && (
                <CreateTaskModal
                    onClose={() => setIsCreateModalOpen(false)}
                    projects={projectsForDropdown}
                    lockedProjectId={lockProject ? projectId : null}
                />
            )}
            {isEditModalOpen && (
                <EditTaskModal
                    onClose={() => {
                        setIsEditModalOpen(false);
                        setSelectedTask(null);
                    }}
                    projects={projectsForDropdown}
                    task={selectedTask}
                    lockedProjectId={lockProject ? projectId : null}
                />
            )}
            {deletingTask && (
                <ConfirmDeleteModal
                    title="Delete task"
                    name={deletingTask.title}
                    detail="All its subtasks, documents, activity and unapproved spend are deleted with it."
                    busy={deleteMutation.isPending}
                    onConfirm={() => deleteMutation.mutate(deletingTask.id)}
                    onClose={() => setDeletingTask(null)}
                />
            )}
        </>
    );

    if (embedded) {
        return (
            <>
                {modals}
                {toolbar}
                <div className="px-4 md:px-6 py-4">
                    {table}
                </div>
            </>
        );
    }

    return (
        <>
            {modals}
            <Layout>
                <div
                    className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${collapsed ? 'left-0 md:left-[76px] right-0' : 'left-0 md:left-64 right-0'
                        }`}>
                    <Header backArrow={false} data={{ name: 'Management Tasks' }} />
                </div>

                <div className="mt-[6rem] px-4 md:px-6">
                    <div className={`fixed top-[4.5rem] z-30 bg-white px-4 md:px-6 py-3 transition-all duration-300 ease-in-out border-b ${collapsed ? 'left-0 md:left-[76px] right-0' : 'left-0 md:left-64 right-0'
                        }`}>
                        {toolbar}
                    </div>

                    <div className="pt-[8rem] md:pt-[5rem]">
                        {table}
                    </div>
                </div>
            </Layout>
        </>
    );
};

export default ManagementLiveTasks;
